import * as React from 'react'
import { usePopper } from 'react-popper'

import { usePopoverMaxHeight } from '../../hooks/use-popover-maxHeight'
import { Button } from '../button'
import { getExtendedDialogState } from '../dialog'
import { Dropdown, DropdownItem, DropdownSeparator, useDropdownState } from '../dropdown'
import { ObjectHash } from '../types'

const DEFAULT_OFFSETS: [number, number] = [5, 5]

function getVirtualRef(top: number, left: number) {
  return {
    getBoundingClientRect(): DOMRect {
      const rect = {
        top: top - window.scrollY,
        x: top - window.scrollY,
        left: left - window.scrollX,
        y: left - window.scrollX,
        bottom: top,
        right: left,
        width: 0,
        height: 0,
      }

      return {
        ...rect,
        toJSON: () => rect,
      }
    },
  }
}

export interface IContextMenuOption {
  type?: never
  id: string
  label: React.ReactNode
  onClick?: () => void
  options?: ContextMenuItem[]
  gtag?: ObjectHash
}

interface IContextMenuDivider {
  type: 'divider'
  label?: React.ReactNode
}

export type ContextMenuItem = IContextMenuOption | IContextMenuDivider

interface ContextMenuProps {
  /**
   * Top coordinate of the click
   */
  top: number

  /**
   * Left coordinate of the click
   */
  left: number

  /**
   * Can limit the max height of the menu
   *
   * @default undefined
   */
  menuMaxHeight?: number

  /**
   * Items visible in the menu
   */
  items: ContextMenuItem[]

  /**
   * Callback to hide the menu
   */
  hide: () => void
}

export function ContextMenu({ top, left, menuMaxHeight, hide, items }: ContextMenuProps) {
  const [popperElement, setPopperElement] = React.useState(null)
  const virtualRef = React.useMemo(() => getVirtualRef(top, left), [left, top])
  const dropdownState = useDropdownState({ placement: 'bottom-start' })
  const { isFullyClosed } = getExtendedDialogState(dropdownState)
  const hasBeenOpen = React.useRef(false)

  const { styles, attributes } = usePopper(virtualRef, popperElement, {
    placement: 'auto-end',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: DEFAULT_OFFSETS,
        },
      },
    ],
  })

  /**
   * This effect handles closing the context menu (parent element) itself
   */
  React.useEffect(() => {
    if (isFullyClosed && hasBeenOpen.current) hide()
  }, [isFullyClosed, hide])

  /**
   * This effect handles opening/closing the inner dropdown
   */
  React.useEffect(() => {
    dropdownState.show()
    hasBeenOpen.current = true // This acts as a way to differentiate the first render

    return () => dropdownState.hide()
    // This needs to run only on mount/unmount for obvious reasons
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      // Popper requires to use state for ref
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ref={setPopperElement}
      {...attributes.popper}
      style={styles.popper}
      className="z-dialog flex min-w-32 flex-col items-stretch space-y-px p-1"
      data-testid="charts-context-menu-body"
    >
      <ContextMenuDropdown
        label={<Button className="invisible max-h-0 max-w-0" />}
        menuMaxHeight={menuMaxHeight}
        state={dropdownState}
        items={items}
        isStatic
      />
    </div>
  )
}

interface ContextMenuInnerProps {
  label: JSX.Element
  items: ContextMenuItem[]
  state: ReturnType<typeof useDropdownState>
  isStatic?: boolean
  menuMaxHeight?: number
}

function ContextMenuInner(
  { label, items, state, isStatic, menuMaxHeight = Number.MAX_SAFE_INTEGER }: ContextMenuInnerProps,
  ref: React.ForwardedRef<HTMLButtonElement>
) {
  const innerState = useDropdownState({ placement: 'right-start' })
  const maxHeightStyle = usePopoverMaxHeight({
    maxHeightLimit: menuMaxHeight,
    hasViewportHeightLimit: true,
    offsets: DEFAULT_OFFSETS,
    state,
  })

  return (
    <Dropdown
      ref={ref}
      label={label}
      state={state}
      aria-label="Context Menu"
      isStatic={isStatic}
      style={maxHeightStyle}
      className="overflow-y-auto"
    >
      {items.map((item, index) => {
        if (item.type === 'divider') {
          return <DropdownSeparator key={index}>{item.label}</DropdownSeparator>
        }

        if (item?.options !== undefined) {
          return (
            <ContextMenuDropdown
              state={innerState}
              label={
                <DropdownItem rightContent="caretRight" data-testid={`dropdown-menu-${item.id}`}>
                  {item.label}
                </DropdownItem>
              }
              items={item.options}
            />
          )
        }

        return (
          <DropdownItem
            key={index}
            onClick={() => {
              item.onClick?.()
              window.gtag?.('event', 'context-menu', Object.assign({ menu_option: item.id }, item.gtag))
            }}
            data-testid={`dropdown-menu-${item.id}`}
            className="shrink-0"
          >
            {item.label}
          </DropdownItem>
        )
      })}
    </Dropdown>
  )
}

const ContextMenuDropdown = React.forwardRef(ContextMenuInner)
